import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Zap, Wifi, Phone, Smartphone, Shield, Flame, Tv } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { http } from "@/lib/api";
import { useVisitorAuth } from "@/context/VisitorAuthContext";

const CUSTOMER_TYPES = ["Homeowner", "Renter", "Moving", "Business", "First-time customer"];
const SERVICE_META = {
    electricity: { label: "Electricity", icon: Zap, note: "Core — Texas plan comparison" },
    internet: { label: "Internet", icon: Wifi, note: "Compare home internet" },
    mobile: { label: "Mobile / cell plan", icon: Smartphone, note: "Lower your phone bill" },
    phone: { label: "Home phone / VoIP", icon: Phone, note: "Landline / VoIP options" },
    home_security: { label: "Home security", icon: Shield, note: "Monitored alarm" },
    natural_gas: { label: "Natural gas", icon: Flame, note: "Where available" },
    tv: { label: "TV / streaming", icon: Tv, note: "Cord-cutting options" },
};

export default function CompleteProfilePage() {
    const { visitor, loading, refresh } = useVisitorAuth();
    const navigate = useNavigate();
    const [zip, setZip] = useState("");
    const [customerType, setCustomerType] = useState("");
    const [phone, setPhone] = useState("");
    const [preferredContact, setPreferredContact] = useState("email");
    const [interests, setInterests] = useState(new Set(["electricity"]));
    const [saving, setSaving] = useState(false);
    const [serviceOptions, setServiceOptions] = useState(Object.keys(SERVICE_META));

    useEffect(() => {
        if (!visitor) return;
        (async () => {
            try {
                const r = await http.get("/visitor/account");
                const l = r.data?.lead || {};
                setZip(l.zip_code || "");
                setCustomerType(l.customer_type && l.customer_type !== "unknown" ? l.customer_type : "");
                setPhone(l.phone || "");
                setPreferredContact(l.preferred_contact || "email");
                setInterests(new Set(l.service_interests?.length ? l.service_interests : ["electricity"]));
                if (r.data?.service_options?.length) setServiceOptions(r.data.service_options);
            } catch (_) {}
        })();
    }, [visitor]);

    if (loading) return null;
    if (!visitor) return <Navigate to="/sign-in" replace />;

    const toggle = (k) => {
        const next = new Set(interests);
        if (next.has(k)) next.delete(k); else next.add(k);
        setInterests(next);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!/^\d{5}$/.test(zip)) {
            toast.error("ZIP must be 5 digits");
            return;
        }
        if (!customerType) {
            toast.error("Pick your customer type");
            return;
        }
        setSaving(true);
        try {
            await http.patch("/visitor/profile", {
                zip_code: zip,
                customer_type: customerType,
                phone: phone || null,
                preferred_contact: preferredContact,
                service_interests: Array.from(interests),
            });
            toast.success("You're all set — Jeffrey will be in touch.");
            await refresh();
            navigate("/my-account", { replace: true });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen" data-testid="complete-profile-page">
            <Navbar />
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
                <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-mono uppercase tracking-wider">
                    Step 2 of 2 · find your options
                </span>
                <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                    Just the essentials.
                </h1>
                <p className="mt-3 text-slate-300 text-sm leading-relaxed">
                    Welcome, {visitor.name?.split(" ")[0] || "friend"}. Tell us where you are and what you'd
                    like us to shop for you. Only electricity is required — everything else is optional.
                </p>

                <form onSubmit={submit} className="mt-8 space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="label-input">ZIP code *</label>
                            <input
                                data-testid="profile-zip-input"
                                value={zip}
                                onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                                inputMode="numeric"
                                placeholder="77001"
                                className="input-dark"
                                required
                            />
                        </div>
                        <div>
                            <label className="label-input">You are a… *</label>
                            <select
                                data-testid="profile-customer-type"
                                value={customerType}
                                onChange={(e) => setCustomerType(e.target.value)}
                                className="input-dark"
                                required
                            >
                                <option value="">Select one</option>
                                {CUSTOMER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="label-input">Phone (optional)</label>
                            <input
                                data-testid="profile-phone-input"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="(713) 555-0100"
                                className="input-dark"
                            />
                        </div>
                        <div>
                            <label className="label-input">Preferred contact</label>
                            <select
                                data-testid="profile-contact-select"
                                value={preferredContact}
                                onChange={(e) => setPreferredContact(e.target.value)}
                                className="input-dark"
                            >
                                <option value="email">Email</option>
                                <option value="text">Text</option>
                                <option value="phone">Phone call</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <div className="label-input">What else can we help you save on? <span className="text-slate-500 font-normal normal-case">(optional)</span></div>
                        <div className="grid sm:grid-cols-2 gap-2 mt-2">
                            {serviceOptions.map((k) => {
                                const meta = SERVICE_META[k] || { label: k, icon: Zap, note: "" };
                                const Icon = meta.icon;
                                const checked = interests.has(k);
                                const required = k === "electricity";
                                return (
                                    <label
                                        key={k}
                                        data-testid={`interest-${k}`}
                                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                                            checked
                                                ? "border-emerald-500/50 bg-emerald-500/10"
                                                : "border-white/10 bg-white/[0.02] hover:border-white/20"
                                        } ${required ? "opacity-90" : ""}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => !required && toggle(k)}
                                            disabled={required}
                                            className="mt-0.5 accent-emerald-500"
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <Icon className="w-4 h-4 text-emerald-400" />
                                                <span className="text-sm font-semibold text-white">
                                                    {meta.label}{required ? " (required)" : ""}
                                                </span>
                                            </div>
                                            <div className="text-xs text-slate-400 mt-0.5">{meta.note}</div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <button
                        type="submit"
                        data-testid="profile-save-btn"
                        disabled={saving}
                        className="btn-primary w-full"
                    >
                        {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        {saving ? "Saving…" : "Find my options"}
                    </button>

                    <p className="text-xs text-slate-500">
                        By continuing you agree to be contacted about options for the services you selected.
                        Opt out any time.
                    </p>
                </form>
            </section>
            <Footer />
        </div>
    );
}
